import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Button, IconButton } from './Button.js';
import { pluralise } from './format.js';
import { Icon, type IconName } from './Icon.js';

// The one transient surface (03-components §6.1): one viewport at --z-toast (above modals so a
// dialog can report its outcome), at most three visible, auto-dismiss for success/info, never for
// errors, paused on hover and focus, dismissible by keyboard, announced by role.

export type ToastTone = 'success' | 'info' | 'warning' | 'error';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastOptions {
  tone?: ToastTone;
  /** A single action, usually Undo. Extends the auto-dismiss to 8s. */
  action?: ToastAction;
  /** Override the auto-dismiss in ms; 0 keeps it until dismissed. Errors ignore this and stay. */
  durationMs?: number;
  /** Replaces an existing toast with the same key instead of stacking a duplicate. */
  key?: string;
}

interface ToastRecord extends Required<Pick<ToastOptions, 'tone'>> {
  id: string;
  message: ReactNode;
  action?: ToastAction;
  durationMs: number;
  key?: string;
}

export interface ToastApi {
  show: (message: ReactNode, options?: ToastOptions) => string;
  success: (message: ReactNode, options?: Omit<ToastOptions, 'tone'>) => string;
  info: (message: ReactNode, options?: Omit<ToastOptions, 'tone'>) => string;
  warning: (message: ReactNode, options?: Omit<ToastOptions, 'tone'>) => string;
  error: (message: ReactNode, options?: Omit<ToastOptions, 'tone'>) => string;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error('useToast must be used within ToastProvider');
  return api;
}

const MAX_VISIBLE = 3;

/** The line above the viewport while older toasts are collapsed: each form a complete string chosen by count (05-content §8). */
export function hiddenToastsText(hidden: number): string {
  return pluralise(hidden, { one: '# earlier notification hidden', other: '# earlier notifications hidden' });
}

const GLYPH: Record<ToastTone, IconName> = { success: 'check', info: 'info', warning: 'alert-triangle', error: 'alert-circle' };

let counter = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const show = useCallback((message: ReactNode, options: ToastOptions = {}): string => {
    const tone = options.tone ?? 'info';
    counter += 1;
    const id = `toast-${counter}`;
    const defaultDuration = tone === 'error' ? 0 : options.action ? 8000 : 5000;
    const record: ToastRecord = {
      id,
      message,
      tone,
      action: options.action,
      durationMs: tone === 'error' ? 0 : options.durationMs ?? defaultDuration,
      key: options.key,
    };
    setToasts((current) => {
      const kept = options.key ? current.filter((toast) => toast.key !== options.key) : current;
      return [...kept, record];
    });
    return id;
  }, []);

  const api = useMemo<ToastApi>(() => ({
    show,
    success: (message, options) => show(message, { ...options, tone: 'success' }),
    info: (message, options) => show(message, { ...options, tone: 'info' }),
    warning: (message, options) => show(message, { ...options, tone: 'warning' }),
    error: (message, options) => show(message, { ...options, tone: 'error' }),
    dismiss,
  }), [show, dismiss]);

  // Older toasts collapse when more than three are visible.
  const visible = toasts.slice(-MAX_VISIBLE);
  const hidden = toasts.length - visible.length;

  return (
    <ToastContext.Provider value={api}>
      {children}
      {typeof document !== 'undefined'
        ? createPortal(
          <div className="toast-viewport" aria-label="Notifications">
            {hidden > 0 ? <p className="toast-viewport__more muted text-xs">{hiddenToastsText(hidden)}</p> : null}
            {visible.map((toast) => <ToastItem key={toast.id} toast={toast} onDismiss={() => dismiss(toast.id)} />)}
          </div>,
          document.body,
        )
        : null}
    </ToastContext.Provider>
  );
}

function ToastItem({ toast, onDismiss }: { toast: ToastRecord; onDismiss: () => void }) {
  const [paused, setPaused] = useState(false);
  const remaining = useRef(toast.durationMs);
  const startedAt = useRef<number>(0);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    if (toast.durationMs <= 0) return;
    if (paused) {
      if (timer.current !== null) {
        window.clearTimeout(timer.current);
        timer.current = null;
        remaining.current = Math.max(0, remaining.current - (Date.now() - startedAt.current));
      }
      return;
    }
    startedAt.current = Date.now();
    timer.current = window.setTimeout(onDismiss, remaining.current);
    return () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    };
  }, [paused, toast.durationMs, onDismiss]);

  const isError = toast.tone === 'error';
  return (
    <div
      className={`toast toast--${toast.tone}`}
      role={isError ? 'alert' : 'status'}
      aria-live={isError ? 'assertive' : 'polite'}
      onPointerEnter={() => setPaused(true)}
      onPointerLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setPaused(false);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') onDismiss();
      }}
    >
      <Icon name={GLYPH[toast.tone]} size={18} className="toast__icon" />
      <div className="toast__message">{toast.message}</div>
      {toast.action ? (
        <Button size="sm" variant="ghost" onClick={() => { toast.action?.onClick(); onDismiss(); }}>{toast.action.label}</Button>
      ) : null}
      <IconButton icon="x" label="Dismiss notification" size="sm" onClick={onDismiss} />
    </div>
  );
}
